import { useEffect, useRef, useState } from 'react';

interface UseScrollAnimationOptions {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
  enterAnimation?: string;  // 화면에 들어올 때 애니메이션
  exitAnimation?: string;   // 화면에서 나갈 때 애니메이션
}

const ANIMATION_COOLDOWN_MS = 600; // 이 시간 안에는 같은 방향으로 상태 변경 안 함

export const useScrollAnimation = (
  animationClass: string,
  options: UseScrollAnimationOptions = {}
) => {
  const { 
    threshold = 0.1, 
    rootMargin = '0px', 
    triggerOnce = true,
    enterAnimation,
    exitAnimation
  } = options;
  
  const elementRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [currentAnimation, setCurrentAnimation] = useState<string>('');
  /** 뷰포트 안에 있으면 true → display 제어용 (코드론 제어 없이 항상 갱신) */
  const [inViewport, setInViewport] = useState(false);

  // ref로 동기 체크 → 경계에서 observer가 연타로 호출되어도 한 번만 반응
  const hasAnimatedRef = useRef(false);
  const lastStateRef = useRef<'in' | 'out' | null>(null);
  const cooldownUntilRef = useRef(0);

  useEffect(() => {
    hasAnimatedRef.current = hasAnimated;
  }, [hasAnimated]);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setInViewport(entry.isIntersecting);
        });

        const now = Date.now();
        if (now < cooldownUntilRef.current) return;

        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (lastStateRef.current === 'in') return;
            lastStateRef.current = 'in';
            cooldownUntilRef.current = now + ANIMATION_COOLDOWN_MS;

            if (triggerOnce && hasAnimatedRef.current) {
              setIsVisible(true);
              setCurrentAnimation('');
            } else {
              setIsVisible(true);
              const enterAnim = enterAnimation || animationClass;
              setCurrentAnimation(`animate__animated ${enterAnim}`);
              if (triggerOnce) {
                hasAnimatedRef.current = true;
                setHasAnimated(true);
              }
            }
          } else {
            // 이미 “나감” 상태로 처리했으면 스킵 → fadeOutUp 중복 재생 방지
            if (lastStateRef.current === 'out') return;
            lastStateRef.current = 'out';
            cooldownUntilRef.current = now + ANIMATION_COOLDOWN_MS;

            setIsVisible(false);
            setCurrentAnimation('');
          }
        });
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, rootMargin, triggerOnce, animationClass, enterAnimation, exitAnimation]);

  const className = enterAnimation || exitAnimation 
    ? currentAnimation 
    : (isVisible ? `animate__animated ${animationClass}` : '');

  return { ref: elementRef, className, inViewport };
};
